import {
  MutableRefObject,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Index, IndexRange, InfiniteLoader } from 'react-virtualized';
import { IData, RowRenderProps, VirtualDataRef } from './VirtualData';
import {
  DataRequestType,
  DataRequestVO,
  DataResponseVO,
} from '@mc/data-filter-react';
import { throttle } from 'lodash-es';

export type VirtualDataProps<
  DATA extends IData<ID>,
  ID extends unknown = {},
> = {
  renderer: number;
  loadMoreRows: (props: IndexRange) => Promise<any>;
  onSectionRendered: (props: SectionRange) => void;
  isRowLoaded: (info: Index) => boolean;
  getRow: (id: ID) => undefined | null | DATA;
  getRowByIndex: (index: number) => undefined | null | DATA;
  getRowRenderProps: (index: number) => RowRenderProps<ID, DATA>;
  infiniteLoaderRef: RefObject<InfiniteLoader>;
};

type SectionRange = {
  startIndex: number;
  stopIndex: number;
};

export const useVirtualData = <
  DATA extends IData<ID>,
  FILTER extends object = {},
  ID extends unknown = {},
>({
  ids,
  ref,
  api,
  getItemId,
  initialCache,
  throttleTime = 1000,
}: {
  ids: ID[];
  ref: any;
  api: (request: DataRequestVO<FILTER>) => Promise<DataResponseVO<DATA>>;
  getItemId?: (item: DATA) => ID;
  throttleTime?: number;
  initialCache?: DATA[];
}): VirtualDataProps<DATA, ID> => {
  const infiniteLoaderRef = useRef<InfiniteLoader>(null);

  // Loaded map cache + first init from inital cache
  const loadedMap = useRef<{ [key: string]: DATA | null }>({});
  const setLoadedMapFromInitialCache = useRef<boolean>(true);
  if (setLoadedMapFromInitialCache) {
    setLoadedMapFromInitialCache.current = false;
    initialCache?.forEach((i) => {
      loadedMap.current[`${i.id}`] = i;
    });
  }

  const [rerender, setRerender] = useState<number>(0);

  const defultGetId = (item: DATA) => item?.id;
  const getId = getItemId ? getItemId : defultGetId;

  const isRowLoaded = useCallback(
    ({ index }: Index) => {
      let val = loadedMap.current[`${ids[index]}`];

      if (val === undefined) {
        return false;
      } else {
        return true;
      }
    },
    [ids]
  );

  const lastRenderedSection = useRef<SectionRange | undefined>(undefined);

  const onSectionRendered = useCallback(
    (props: SectionRange) => {
      lastRenderedSection.current = props;

      doFetch();
    },
    [ids]
  );

  // const loadMoreCache = useRef<LoadMoreCache[]>([]);
  // const loadMoreCachePromise = useRef<Promise<any>[]>([]);

  const doFetchInner = async () => {
    // Vytvoříme se dvě okénka - větší (start-250 - stop+250) a menší (start-200 - stop+200)
    // Pokud v menším okénku chybí data a je nutné něco načíst, načteme data pro větší okénko
    if (lastRenderedSection.current) {
      const start = Math.max(0, lastRenderedSection.current.startIndex - 250);
      const stop = Math.min(
        lastRenderedSection.current?.stopIndex + 250,
        ids.length
      );
      const start2 = Math.max(0, lastRenderedSection.current.startIndex - 200);
      const stop2 = Math.min(
        lastRenderedSection.current?.stopIndex + 200,
        ids.length
      );
      let range2NeedFetch = false;
      for (let a = start2; a < stop2; a++) {
        let id = ids[a];
        if (loadedMap.current[`${id}`] === undefined) {
          range2NeedFetch = true;
          break;
        }
      }

      if (range2NeedFetch) {
        const idsForLoad: ID[] = [];
        for (let a = start; a < stop; a++) {
          let id = ids[a];
          if (loadedMap.current[`${id}`] === undefined) {
            idsForLoad.push(id);
          }
        }

        const result = await api({
          requestType: DataRequestType.DATA,
          idsRequest: {
            // @ts-ignore
            ids: idsForLoad,
          },
        });

        const resultMap: { [key: string]: DATA } = {};
        result.rows?.forEach((item) => {
          // @ts-ignore
          resultMap[`${getId(item)}`] = item;
        });

        idsForLoad.forEach((id) => {
          const item = resultMap[`${id}`];
          loadedMap.current[`${id}`] = item || null;
        });

        setRerender(new Date().getMilliseconds());
      }

      // loadMoreCache.current = [];
      // loadMoreCachePromise.current = [];
    }
  };

  const doFetch = useCallback(
    throttle(doFetchInner, throttleTime, { leading: true, trailing: true }),
    [ids, api]
  );

  const doFetchNoThrottle = useCallback(() => doFetchInner(), [ids, api]);

  const loadMoreRows = useCallback(
    async ({}: /*startIndex, stopIndex*/ IndexRange) => {
      // console.log(
      //   'Load more: ',
      //   '[',
      //   startIndex,
      //   '->',
      //   stopIndex,
      //   ']',
      //   'count',
      //   stopIndex - startIndex
      // );
      // loadMoreCache.current.push({ startIndex, stopIndex });
      // loadMoreCachePromise.current;
      doFetch();
    },
    [ids, api, doFetch]
  );

  // const loadMoreRowsOld = useCallback(
  //   async ({ startIndex, stopIndex }: IndexRange) => {
  //     const idsForLoad = ids.slice(startIndex, stopIndex + 1);
  //     console.log(
  //       'Load more: ',
  //       '[',
  //       startIndex,
  //       '->',
  //       stopIndex,
  //       ']',
  //       'count',
  //       stopIndex - startIndex
  //     );
  //
  //     const result = await api({
  //       requestType: DataRequestType.DATA,
  //       idsRequest: {
  //         // @ts-ignore
  //         ids: idsForLoad,
  //       },
  //     });
  //
  //     const resultMap: { [key: string]: DATA } = {};
  //     result.rows?.forEach((item) => {
  //       // @ts-ignore
  //       resultMap[`${getId(item)}`] = item;
  //     });
  //
  //     // console.log('Loaded', resultMap);
  //
  //     idsForLoad.forEach((id) => {
  //       const item = resultMap[`${id}`];
  //       loadedMap.current[`${id}`] = item || null;
  //     });
  //     // console.log('MM', loadedMap.current);
  //   },
  //   [ids, api]
  // );

  useEffect(() => {
    loadedMap.current = {};
    // console.log('RESET');
    infiniteLoaderRef.current?.resetLoadMoreRowsCache(true);
    setRerender(new Date().getMilliseconds());
    doFetchNoThrottle();
  }, [ids]);

  if (ref) {
    const value: VirtualDataRef<ID> = {
      invalidate: (ids?: ID[]) => {
        if (ids === undefined) {
          loadedMap.current = {};
        } else {
          ids.forEach((id) => {
            delete loadedMap.current[`${id}`];
          });
        }
        infiniteLoaderRef.current?.resetLoadMoreRowsCache(true);
        setRerender(new Date().getMilliseconds());
      },
    };
    if (typeof ref === 'function') {
      ref(value);
    } else {
      (ref as MutableRefObject<any>).current = value;
    }
  }

  return {
    renderer: rerender,
    loadMoreRows,
    onSectionRendered,
    isRowLoaded,
    getRow: (id) => {
      const strId = `${id}`;
      return loadedMap.current[strId];
    },
    getRowByIndex: (index) => {
      const id = ids[index];
      const strId = `${id}`;
      return loadedMap.current[strId];
    },
    getRowRenderProps: (index) => {
      const id = ids[index];
      const strId = `${id}`;
      const row = loadedMap.current[strId];

      return {
        id,
        isFetching: row === undefined,
        isDeleted: row === null,
        isFetched: row !== undefined,
        row,
        index,
      };
    },
    //@ts-ignore
    infiniteLoaderRef,
  };
};
